import { trackFestaEvent } from './eventTracker';

// backend/config/festa_campaign.py 와 값을 맞춘다.
export const FESTA_CAMPAIGN = 'b2g_school_adoption_2026q4';
export const FESTA_LANDING_PATH = '/events/year-end-festa';
export const FESTA_COUPON_CODE = 'FESTA30';
export const FESTA_PENDING_COUPON_KEY = 'pending_coupon_code';
export const FESTA_BUDGET_EVENT_TYPE = 'festa_budget_2027';
export const FESTA_LANDING_PAGE_TOKEN = 'year_end_festa';
export const FESTA_CLOSED_REDIRECT = '/pricing?from=festa_closed';

// 실제 마감만 보여 준다(가짜 긴급성 금지). 쿠폰 등록은 서버의 FESTA30 valid_until이 막는다.
export const FESTA_COUPON_START = new Date('2026-11-23T00:00:00+09:00');
export const FESTA_COUPON_END = new Date('2026-12-31T23:59:59+09:00');
export const FESTA_BUDGET_FORM_END = new Date('2027-02-28T23:59:59+09:00');

export const FESTA_COUPON_START_LABEL = '11월 23일';
export const FESTA_COUPON_END_LABEL = '12월 31일';
export const FESTA_BUDGET_FORM_END_LABEL = '2027년 2월 28일';
export const FESTA_SCHOOL_PAY_DEADLINE_LABEL = '2027년 4월 30일';

export const FESTA_PRICES = {
  personalMonth: 12000,
  personalMonthList: 24000,
  school12m: 100000,
  school12mList: 200000,
};

export const FESTA_PHASES = {
  BEFORE: 'before',
  LIVE: 'live',
  BUDGET: 'budget',
  CLOSED: 'closed',
};

const PREVIEW_PARAM = 'festa_preview';

export const getFestaPhase = (now = new Date()) => {
  if (now < FESTA_COUPON_START) return FESTA_PHASES.BEFORE;
  if (now <= FESTA_COUPON_END) return FESTA_PHASES.LIVE;
  if (now <= FESTA_BUDGET_FORM_END) return FESTA_PHASES.BUDGET;
  return FESTA_PHASES.CLOSED;
};

// 검수용: ?festa_preview=before|live|budget|closed 로 단계를 강제한다. 쿠폰 등록 가능 여부는 서버가 따로 판정한다.
export const resolveFestaPhase = (search = '', now = new Date()) => {
  try {
    const preview = new URLSearchParams(search).get(PREVIEW_PARAM);
    if (preview && Object.values(FESTA_PHASES).includes(preview)) return preview;
  } catch {
    // 잘못된 쿼리는 실제 날짜로 판정한다.
  }
  return getFestaPhase(now);
};

// 오픈 전에는 오픈까지, 오픈 뒤에는 마감까지 센다.
export const getFestaCountdown = (now = new Date()) => {
  const before = now < FESTA_COUPON_START;
  const target = before ? FESTA_COUPON_START : FESTA_COUPON_END;
  const remaining = Math.max(0, target.getTime() - now.getTime());
  const totalSeconds = Math.floor(remaining / 1000);
  return {
    before,
    done: remaining === 0,
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
  };
};

// 링크 공유 중 쿠폰 파라미터가 빠져도 "가입하면 30일" 약속이 지켜지게, 쿠폰 기간에는 FESTA30을 기본 저장한다.
// 다른 캠페인 쿠폰(AI30 등) 파라미터로 들어오면 덮어쓰지 않는다. 계정당 1회는 서버가 강제한다.
export const storeFestaPendingCoupon = (search = '', phase = FESTA_PHASES.LIVE) => {
  if (phase !== FESTA_PHASES.LIVE) return false;
  try {
    const coupon = (new URLSearchParams(search).get('coupon') || '').trim().toUpperCase();
    if (coupon && coupon !== FESTA_COUPON_CODE) return false;
    localStorage.setItem(FESTA_PENDING_COUPON_KEY, FESTA_COUPON_CODE);
    return true;
  } catch {
    return false;
  }
};

export const buildFestaSignupUrl = (redirect = '/create-saenggibu') => (
  `/auth?tab=signup&redirect=${encodeURIComponent(redirect)}&coupon=${FESTA_COUPON_CODE}`
);

export const FESTA_BUDGET_SOURCES = [
  { id: 'this_year', label: '올해 남은 예산' },
  { id: 'next_year', label: '2027년 예산' },
  { id: 'unknown', label: '아직 몰라요' },
];

export const FESTA_TRAINING_TIMES = [
  { id: 'december', label: '12월 방학 전' },
  { id: 'february', label: '2월 새 학년 준비 기간' },
  { id: 'both', label: '둘 다' },
  { id: 'none', label: '괜찮아요' },
];

export const FESTA_FEATURES = [
  { id: 'saenggibu', label: '생기부 작성' },
  { id: 'scoring', label: '서술형 채점' },
  { id: 'lesson', label: '수업 자료·PPT' },
  { id: 'quiz', label: '문제 만들기' },
  { id: 'review', label: '학교 생기부 검토' },
];

const labelOf = (options, id) => options.find((option) => option.id === id)?.label || '';

export const buildFestaBudgetMessage = ({
  teacherCount,
  budget = '',
  budgetSource = '',
  features = [],
  extraFeature = '',
  trainingTime = '',
}) => {
  const clean = (value) => String(value || '').replace(/[|\s]+/g, ' ').trim();
  const parts = [`인원: ${clean(teacherCount)}명`];
  if (clean(budget)) parts.push(`예산: ${clean(budget)}`);
  if (budgetSource) parts.push(`예산 연도: ${labelOf(FESTA_BUDGET_SOURCES, budgetSource)}`);
  const featureLabels = features.map((id) => labelOf(FESTA_FEATURES, id)).filter(Boolean);
  if (featureLabels.length) parts.push(`원하는 기능: ${featureLabels.join(', ')}`);
  if (clean(extraFeature)) parts.push(`추가 기능: ${clean(extraFeature).slice(0, 300)}`);
  if (trainingTime) parts.push(`연수: ${labelOf(FESTA_TRAINING_TIMES, trainingTime)}`);
  return `[학교예산상담] ${parts.join(' | ')}`;
};

export const trackFestaLanding = (search = '', phase = FESTA_PHASES.LIVE) => {
  try {
    const params = new URLSearchParams(search);
    return trackFestaEvent('landing_viewed', {
      entry: params.get('utm_medium') || 'direct',
      variantId: params.get('utm_content') || 'control',
      stage: phase,
    });
  } catch {
    return false;
  }
};
